import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { uniqueBy } from '~/utils/arrayUtils';

import { assetKey, roomAssetKey } from './types';
import type { CategoryRoom, NetRateFormulaState, OptionState, RoomAssetLink, RoomPricing } from './types';

type ProrateInput = {
  isAsiaOnly: boolean;
  lengthsOfStay: Array<number>;
};

type SetSellPriceInput = ProrateInput & {
  nights: number;
  value: string;
};

export const ASIA_ONLY_LOS = 3;

// Tied to the hardcoded `id: 1` room in DEFAULT_OPTION_STATE below — keep both in sync.
export const FIRST_FREE_ROOM_TYPE_ID = 2;

export const SELL_ADJUSTMENT_MODES = ['none', 'fixed', 'daily', 'pct'] as const;

export type SellAdjustmentMode = (typeof SELL_ADJUSTMENT_MODES)[number];

export function roundToNine(value: number): number {
  return Math.round((value - 9) / 10) * 10 + 9;
}

export const round2 = (value: number) => Math.round(value * 100) / 100;

export type SellAdjustment = {
  mode: SellAdjustmentMode;
  amount: number;
  nights?: number;
};

export function adjustSellKeepingMargin(sell: number, net: number, adjustment: SellAdjustment) {
  const { mode, amount, nights = 1 } = adjustment;
  if (mode === 'none' || amount === 0 || sell <= 0) {
    return { sell: round2(sell), net: round2(net) };
  }

  const marginPct = (sell - net) / sell;
  const adjusted =
    mode === 'fixed' ? sell + amount : mode === 'daily' ? sell + amount * nights : sell * (1 + amount / 100);
  const newSell = adjusted > 0 ? roundToNine(adjusted) : round2(sell);
  return { sell: newSell, net: round2(newSell * (1 - marginPct)) };
}

export function extraNightSellFromLos(sell: number, nights: number): string {
  if (!(sell > 0) || !(nights > 0)) {
    return '';
  }
  const perNight = Math.round(sell / nights / 10) * 10;
  return perNight > 0 ? String(perNight) : '';
}

export function prorateSellByLos(
  current: Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'>,
  { lengthsOfStay, isAsiaOnly }: ProrateInput,
): Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'> {
  const sortedLos = [...lengthsOfStay].sort((a, b) => a - b);
  const baseLos = sortedLos.find((los) => Number(current.sellByLos[los]) > 0);
  if (baseLos === undefined) {
    return { sellByLos: current.sellByLos, sellEditedByLos: current.sellEditedByLos };
  }

  const basePrice = Number(current.sellByLos[baseLos]);
  const sellByLos = { ...current.sellByLos };
  for (const los of sortedLos) {
    if (los === baseLos || current.sellEditedByLos[los] || (isAsiaOnly && los !== ASIA_ONLY_LOS)) {
      continue;
    }
    const rounded = roundToNine((basePrice / baseLos) * los);
    sellByLos[los] = rounded < 0 ? '' : String(rounded);
  }

  return { sellByLos, sellEditedByLos: current.sellEditedByLos };
}

export function setSellPrice(
  current: Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'>,
  { nights, value, lengthsOfStay, isAsiaOnly }: SetSellPriceInput,
): Pick<RoomPricing, 'sellByLos' | 'sellEditedByLos'> {
  const edited = {
    sellByLos: { ...current.sellByLos, [nights]: value },
    sellEditedByLos: { ...current.sellEditedByLos, [nights]: value.trim() !== '' },
  };

  if (nights !== Math.min(...lengthsOfStay)) {
    return edited;
  }

  const prorated = prorateSellByLos(
    { sellByLos: edited.sellByLos, sellEditedByLos: { ...edited.sellEditedByLos, [nights]: false } },
    { isAsiaOnly, lengthsOfStay },
  );

  return { sellByLos: prorated.sellByLos, sellEditedByLos: edited.sellEditedByLos };
}

export function applyNetFormulaToPricing(
  pricingByRoomId: Record<number, RoomPricing>,
  {
    applyToExtraNight,
    lengthsOfStay,
    netFraction,
    roomIds,
  }: { applyToExtraNight: boolean; lengthsOfStay: Array<number>; netFraction: number; roomIds: Array<number> },
): Record<number, RoomPricing> {
  const updated = { ...pricingByRoomId };
  for (const [idKey, pricing] of Object.entries(pricingByRoomId)) {
    const id = Number(idKey);
    if (!roomIds.includes(id)) {
      continue;
    }
    const netByLos = { ...pricing.netByLos };
    // Every active LOS gets a net rate, even ones with no sell price yet (treated as 0).
    // Net rates are whole numbers, so round to the nearest dollar rather than the nearest cent.
    for (const nights of lengthsOfStay) {
      netByLos[nights] = String(Math.round((Number(pricing.sellByLos[nights]) || 0) * netFraction));
    }
    updated[id] = {
      ...pricing,
      netByLos,
      extraNightNet: applyToExtraNight
        ? String(Math.round((Number(pricing.extraNightSell) || 0) * netFraction))
        : pricing.extraNightNet,
    };
  }
  return updated;
}

export function clearNonAsiaOnlyPricing(pricing: RoomPricing): RoomPricing {
  const keepAsiaOnlyLos = <T>(byLos: Record<number, T>) =>
    Object.fromEntries(Object.entries(byLos).filter(([los]) => Number(los) === ASIA_ONLY_LOS));

  return {
    ...pricing,
    sellByLos: keepAsiaOnlyLos(pricing.sellByLos),
    sellEditedByLos: keepAsiaOnlyLos(pricing.sellEditedByLos),
    netByLos: keepAsiaOnlyLos(pricing.netByLos),
  };
}

export function defaultRoomPricing(occupancy: string): RoomPricing {
  return {
    allocation: '',
    occupancy,
    sellByLos: {},
    sellEditedByLos: {},
    netByLos: {},
    extraNightSell: '',
    extraNightSellEdited: false,
    extraNightNet: '',
    otaPrice: '',
  };
}

export const DEFAULT_NET_RATE_FORMULA_STATE: NetRateFormulaState = {
  netPercent: '0',
  applyToExtraNight: true,
  scope: 'all',
  selectedRoomIds: [],
};

export const DEFAULT_OPTION_STATE: OptionState = {
  lengthsOfStay: [3],
  roomsByCategory: [[{ id: 1, assetName: '', asiaOnly: false }], [], [], []], // id: 1 — see FIRST_FREE_ROOM_TYPE_ID
  selectedLos: 3,
  pricingByRoomId: {},
  netRateFormula: DEFAULT_NET_RATE_FORMULA_STATE,
};

export function collectRoomAssets(
  deals: Array<OpportunityRelatedDeal> = [],
  created: Array<RoomAssetLink> = [],
): Array<RoomAssetLink> {
  const fromDeals = deals
    .map((deal) => deal.room_asset)
    .filter((asset): asset is OpportunityRoomAsset => !!asset?.name)
    .map((asset) => ({ sfid: asset.sfid ?? undefined, name: asset.name }));

  return uniqueBy([...fromDeals, ...created], (asset) => asset.sfid ?? asset.name);
}

// Two base rows on one asset make the forecast read the same packages cell twice and double every total.
export function takenRoomAssetKeys(basicRooms: Array<CategoryRoom>, keepRoomId?: number): Set<string> {
  return new Set(basicRooms.filter((room) => room.assetName && room.id !== keepRoomId).map(roomAssetKey));
}

export function selectableRoomAssets(
  assets: Array<RoomAssetLink>,
  basicRooms: Array<CategoryRoom>,
  keepRoomId?: number,
): Array<RoomAssetLink> {
  const taken = takenRoomAssetKeys(basicRooms, keepRoomId);

  return assets.filter((asset) => !taken.has(assetKey(asset)));
}
